<template>
  <div class="mb-4">
    <div class="row d-flex justify-content-center">
      <action-button
        v-if="documents.length === 0"
        class="border w-40 btn bg-white"
        i="fas fa-file-plus color-primary-1"
        text="Presione aquí para anejar documento"
        @click="startAddDocument"
      />
    </div>
    <validation-observer ref="documentFormRef" tag="form" novalidate>
      <div
        v-for="(item, index) in documents"
        :key="`document-row-${index}`"
        class="row bg-secondary-13 rounded mb-2 p-2"
      >
        <div class="col-4">
          <div class="color-gray-6 mb-2">Tipo de Documento:</div>
          <validation-provider
            v-if="!item.added"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-drop-down
              v-model="item.selectedDocumentType"
              :options="availableDocumentTypes"
              fieldkey="documentTypeId"
              fieldname="documentTypeName"
              placeholder="Seleccione"
              :error="errors[0]"
              custom-error-msg="Favor seleccionar el tipo de documento."
            />
          </validation-provider>
          <div v-else>
            {{ item.selectedDocumentType.documentTypeName }}
          </div>
        </div>
        <div class="col-4">
          <div class="color-gray-6 mb-2">Archivo:</div>
          <validation-provider
            v-if="!item.added"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-file-input
              ref="attachedDocument"
              v-model="item.file"
              :attachment-id="item.documentId"
              accept=".pdf"
              required
              display-style="normal"
              label-position="left"
              :on-download="() => {}"
              :error="errors[0]"
              custom-error-msg="Favor seleccionar un documento."
              @input="onChangeFile(item, $event)"
            />
          </validation-provider>
          <div v-else>
            {{ item.filename }}
          </div>
        </div>
        <div class="col-4 d-flex justify-content-end align-items-end">
          <div v-if="!item.added">
            <action-button
              id="modal-btn-cancelar"
              variant="outline-danger"
              i="fas fa-times-circle"
              @click="cancelHandler(index)"
            >
              Cancelar
            </action-button>
            <action-button
              id="modal-btn-save"
              variant="success ml-3"
              i="fas fa-save"
              @click="saveHandler(index)"
            >
              Guardar
            </action-button>
          </div>
          <drop-down
            v-else
            :items="getDocumentActions(item)"
            class="d-sm-block"
          />
        </div>
      </div>
      <div v-if="enableAdd && !disabled" class="d-flex justify-content-end">
        <action-button
          variant="success px-3 mr-2"
          i="fas fa-file-plus"
          @click="addHandler()"
        >
          Añadir
        </action-button>
      </div>
    </validation-observer>
    <error
      variant="no-border"
      :message="needToAddRequiredDocsMessage"
      class="mt-3"
    />
  </div>
</template>

<script>
import alertMixin from 'utils/mixins/alert.mixin';
import ActionButton from 'ActionButton';
import CustomFileInput from 'CustomFileInput';
import CustomDropDown from 'custom-drop-down';
import DropDown from 'dropdown';
import Error from 'custom-error';
import documentsService from '@/services/documents';

export default {
  name: 'DocumentSelector',
  mixins: [alertMixin],
  components: {
    ActionButton,
    CustomFileInput,
    CustomDropDown,
    DropDown,
    Error,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      deafult: () => false,
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.documents = newVal
            .filter((x) => x.itemAttachmentId)
            .map((x) => ({
              ...x,
              added: true,
              selectedDocumentType: {
                documentTypeId: x.documentTypeId,
                documentTypeName: x.documentTypeName,
              },
            }));
        }
      },
    },
  },
  data() {
    return {
      documents: [],
      needToAddRequiredDocs: false,
      needToAddRequiredDocsMessage: null,
    };
  },
  computed: {
    availableDocumentTypes() {
      const addedDocumentTypesIds = this.documents
        .filter((x) => x.added)
        .map((x) => x.selectedDocumentType?.documentTypeId);
      return this.value.filter(
        (x) => !addedDocumentTypesIds.includes(x.documentTypeId)
      );
    },
    getDocumentActions() {
      const self = this;
      return (document) => [
        {
          label: 'Descargar',
          icon: 'fas fa-file-download text-green pr-1',
          click: () => this.downloadFile(document),
        },
        {
          label: 'Eliminar',
          icon: 'fas fa-times-circle text-red pr-1',
          visible: !self.disabled,
          click: () => {
            const index = self.documents.findIndex((doc) => doc === document);
            self.documents.splice(index, 1);
            this.$emit('on-selected-document', document);
          },
        },
      ];
    },
    isLastIndex() {
      return (idx) =>
        this.documents.length - 1 === idx ||
        this.documents.length !== this.value.length;
    },
    enableAdd() {
      const _added = this.documents.length;
      const documentTypes = this.value.length;
      return (
        this.documents.filter((x) => x.added).length > 0 &&
        _added != documentTypes
      );
    },
  },
  methods: {
    downloadFile(document) {
      if (document.itemAttachmentId) {
        documentsService.downloadFromServer(document.itemAttachmentId);
      } else {
        let file = {
          filename: document.filename,
          content: document.file,
          contentType: 'application/pdf',
        };
        documentsService.downloadFile(file);
      }
    },
    startAddDocument() {
      this.documents.push({});
    },
    async saveHandler() {
      const isValid = await this.$refs.documentFormRef.validate();
      if (!isValid) {
        this.ShowToast('Error', 'Informacion incompleta.', 'error');
        return;
      }
      this.documents = this.documents.map((x) => ({ ...x, added: true }));
      this.validate();
    },
    cancelHandler(itemIndex) {
      this.documents.splice(itemIndex, 1);
    },
    async addHandler() {
      const isValid = await this.$refs.documentFormRef.validate();
      if (!isValid) {
        this.ShowToast('Error', 'Informacion incompleta.', 'error');
        return;
      }
      this.documents = this.documents.map((x) => ({ ...x, added: true }));
      if (this.documents.length != this.value.length) this.documents.push({});
    },
    validate() {
      const missingRequiredDocs =
        this.documents.filter((x) => x.added || x.itemAttachmentId).length == 0;

      this.needToAddRequiredDocsMessage = missingRequiredDocs
        ? 'Es requerido añadir al menos un documento de justificación.'
        : '';

      return !missingRequiredDocs;
    },
    async onChangeFile(item, file) {
      if (item && file) {
        this.$emit('on-selected-document', item);
        item.filename = file.name;
        await this.$forceUpdate();
      }
    },
  },
};
</script>

<style></style>
